import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMediaQuery } from 'react-responsive'
import { createFilter } from 'react-select'
import { Button } from '../../../components/button'
import { Mobile } from '../../../components/media'
import Modal from '../../../components/modal'
import { Select } from '../../../components/inputs'
import {
  changeFinancialPreferences,
  getFinancialPreferencesKey,
  getFinancialPreferences,
} from '../../../api-client/purchase'

const defaultPreferences = {
  annualMileage: 15000,
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    border: state.selectProps.error
      ? '0.2rem solid var(--alert-color) !important'
      : '0.2rem solid var(--secondary-input-bg) !important',
  }),
}

const filterConfig = {
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
  matchFrom: 'start',
}

const mileageOptions = [
  {
    label: '10k',
    value: 10000,
  },
  {
    label: '12k',
    value: 12000,
  },
  {
    label: '15k',
    value: 15000,
  },
  {
    label: '18k',
    value: 18000,
  },
]

export default function FinancialPreferences({ onClose }) {
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' })
  const [financialPreferences, setFinancialPreferences] = useState(defaultPreferences)

  const { isFetching } = useQuery({
    queryKey: getFinancialPreferencesKey,
    queryFn: getFinancialPreferences,
    onSuccess: (data) => setFinancialPreferences(data.financialPreferences),
  })

  const handleChange = (value) => (name) =>
    setFinancialPreferences((prev) => ({ ...prev, [name]: value }))

  const { mutate, isLoading: mutating } = useMutation({
    mutationFn: changeFinancialPreferences,
    onSuccess: onClose,
  })

  const handleSave = () => {
    mutate(financialPreferences)
  }

  const isBusy = mutating || isFetching

  return (
    <Modal onClose={onClose} hideCloseBtn={isMobile}>
      <Mobile>
        <button className='flatBtn backToProfile' onClick={onClose}>
          &#60; Back to Profile
        </button>
      </Mobile>

      <div className='wrapper'>
        <h1 className='title'>Financial Preferences</h1>

        <section id='communicationMedium'>
          <h2 className='description'>How many miles do you drive per year?</h2>

          <div className='item'>
            <label htmlFor='milesPerYear'>Miles</label>
            <Select
              styles={selectStyles}
              id='milesPerYear'
              className='milesPerYearSelect'
              filterOption={createFilter(filterConfig)}
              options={mileageOptions}
              value={{
                value: financialPreferences.annualMileage,
                label: financialPreferences.annualMileage.toString().replace(/000$/, 'k'), //Only want to replace 000 at the end, 10000 gets messed up without it
              }}
              onChange={({ value }) => handleChange(value)('annualMileage')}
            />
          </div>
        </section>

        <Button className='saveBtn' disabled={isBusy} onClick={handleSave}>
          Save
        </Button>
      </div>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 1.6rem 2rem;
          background: #fff;
          border-radius: 1rem;
          font-size: 1.6rem;
          min-height: 35rem;

          :global(.milesPerYearSelect) {
            width: 15rem;
          }

          :global(.saveBtn) {
            display: block;
            margin: 4rem auto 0;
          }
        }

        .title {
          text-align: center;
          font-size: 3.2rem;
        }

        .description {
          margin: 1.5rem auto 0;
          text-align: center;
          font-size: 1.6rem;
          font-weight: 400;
        }

        .item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 2.1rem 0;

          label {
            font-weight: 700;
          }

          &:not(:last-child) {
            border-bottom: 0.1rem solid var(--separator-line-color);
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            height: 100%;
            padding: 5rem 1.35rem 6rem;

            :global(.saveBtn) {
              position: fixed;
              bottom: 2.1rem;
              left: 1.2rem;
              width: calc(100vw - 2.4rem);
            }
          }

          .backToProfile {
            margin: 1rem 0 0 1.3rem;
            color: #a5a8b9;
            line-height: 1.6rem;
            font-size: 1.2rem;
            font-weight: bold;
          }

          .title {
            line-height: 2.4rem;
            font-size: 1.8rem;
          }

          .description {
            margin: 0.2rem 0 3rem;
            line-height: 1.9rem;
            font-size: 1.4rem;
          }

          .item {
            padding: 2rem 0 2.45rem;

            label {
              font-size: 1.4rem;
            }
          }

          #notificationTypes.description {
            margin: 2rem 0 2.7rem;
          }
        }
      `}</style>
    </Modal>
  )
}
